import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { IPaymentMethodsSettings } from '../../../../common/models/settings'
import FormGroup from '../../../../core/components/FormGroup'
import {
  getPaymentMethodsSettings,
  updatePaymentMethodsSettings,
} from '../../../../core/services/festival-settings'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Checkbox } from '@/components/ui'

interface IPaymentMethodsValues {
  mandatoryDeliveryAddress: boolean
}

export const PaymentMethods: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()

  const [paymentMethods, setPaymentMethods] =
    useState<IPaymentMethodsSettings>()
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getPaymentMethodsSettings()
        if (resp.status === 200) {
          setPaymentMethods(resp.data)
          setValue('mandatoryDeliveryAddress', resp.data.mandatoryDeliveryAddress)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    mandatoryDeliveryAddress: yup.boolean().required(),
  })

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<IPaymentMethodsValues>({
    mode: 'onChange',
    resolver: yupResolver<IPaymentMethodsValues>(schema),
    defaultValues: {
      mandatoryDeliveryAddress: false,
    },
  })

  const submit = async (values: IPaymentMethodsValues) => {
    try {
      const { status } = await updatePaymentMethodsSettings(values)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (paymentMethods) {
      setValue(
        'mandatoryDeliveryAddress',
        paymentMethods.mandatoryDeliveryAddress,
      )
    }
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
        <form
          id="settings-payment-methods-form"
          onSubmit={handleSubmit(submit)}
        >
          <h3 className="text-title-xs text-monochrome-1 mb-4">
            Payment Methods
          </h3>
          <FormGroup
            label="Delivery Address Mandatory"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'mandatoryDeliveryAddress',
            }}
          >
            <Controller
              name="mandatoryDeliveryAddress"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="mandatoryDeliveryAddress"
                  ref={field.ref}
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                  disabled={!hasEditPermission}
                />
              )}
            />
          </FormGroup>
        </form>

        <div className="flex justify-end gap-2 border-t border-monochrome-120 mt-4 [&>*]:w-[154px] pt-4">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            form="settings-payment-methods-form"
            type="submit"
            variant="primary"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
